// Menu des langues, hors de la barre de navigation (z-index), ouvert par le
// bouton de LanguageSwitcher (paramètres du compte) via la fonction globale
// window.toggleLanguageDropdown(). Données : ReactShell::LanguageDropdown.
//
// Sa <style> est globale (#main-content en position relative, débordements
// visibles de la barre…) : elle est rendue avec le menu.
import { Fragment, useEffect, useRef, useState } from 'react'
import { usePage } from '@inertiajs/react'
import { useShared } from '@/hooks/useShared'

type DropdownLanguage = { flag: string; name: string; path: string; active: boolean }

export type LanguageDropdownData = { languages: DropdownLanguage[] }

declare global {
  interface Window {
    toggleLanguageDropdown?: () => void
  }
}

const DROPDOWN_WIDTH = 140 // min-width du menu (CSS)
// Sauts de ligne entre les éléments : sans effet à l'écran, ils séparent les
// mots du texte du menu masqué.
const NL = '\n'

const toggleButton = () => document.querySelector<HTMLElement>('.language-dropdown-toggle')

export default function LanguageDropdown({ data }: { data: Record<string, any> }) {
  const { languages } = data as LanguageDropdownData
  const { csrfToken } = useShared()
  const { url } = usePage()
  // Ouvert pour l'adresse courante seulement : une navigation le referme.
  const [openAt, setOpenAt] = useState<string | null>(null)
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null)
  const open = openAt === url
  const menuRef = useRef<HTMLDivElement>(null)
  const state = useRef({ open, url })
  state.current = { open, url }

  useEffect(() => {
    const close = () => {
      setOpenAt(null)
      toggleButton()?.setAttribute('aria-expanded', 'false')
    }

    window.toggleLanguageDropdown = () => {
      const toggle = toggleButton()
      if (!menuRef.current || !toggle) {
        console.error('Dropdown elements not found')
        return
      }
      if (state.current.open) {
        close()
        return
      }
      // Sous le bouton, bord droit aligné sur le sien, à 10 px des bords de
      // l'écran au plus près.
      const rect = toggle.getBoundingClientRect()
      const left = Math.max(10, Math.min(rect.right - DROPDOWN_WIDTH, window.innerWidth - DROPDOWN_WIDTH - 10))
      setPosition({ top: rect.bottom + 4, left })
      setOpenAt(state.current.url)
      toggle.setAttribute('aria-expanded', 'true')
    }

    const onClick = (e: MouseEvent) => {
      const switcher = document.getElementById('languageDropdown')
      if (switcher && !switcher.contains(e.target as Node)) close()
    }
    const onKeydown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
    }
    document.addEventListener('click', onClick)
    document.addEventListener('keydown', onKeydown)
    return () => {
      document.removeEventListener('click', onClick)
      document.removeEventListener('keydown', onKeydown)
      delete window.toggleLanguageDropdown
    }
  }, [])

  return (
    <>
      <div
        ref={menuRef}
        id="languageDropdownMenu"
        className={`language-dropdown-menu${open ? ' show' : ''}`}
        style={{
          display: 'none',
          ...(position && { top: `${position.top}px`, left: `${position.left}px`, zIndex: 999999 }),
        }}
      >
        {NL}
        <ul>
          {NL}
          {languages.map((lang, i) => (
            <Fragment key={lang.path}>
              {i > 0 && NL}
              <li>
                {NL}
                {/* Formulaire natif : tout le document change de langue. */}
                <form className="language-form" action={lang.path} acceptCharset="UTF-8" method="post">
                  <input type="hidden" name="authenticity_token" value={csrfToken} autoComplete="off" />
                  {NL}
                  <button type="submit" className={`dropdown-item language-option${lang.active ? ' active' : ''}`}>
                    {NL}
                    <span className="flag">{lang.flag}</span>
                    {NL}
                    <span className="language-name">{lang.name}</span>
                    {NL}
                  </button>
                  {NL}
                </form>
              </li>
            </Fragment>
          ))}
          {NL}
        </ul>
        {NL}
      </div>

      <style>{`
  /* Language Switcher Dropdown Styles */
  .language-switcher-dropdown {
    position: relative;
    display: inline-block;
    z-index: 2147483647; /* Maximum z-index for container */
    overflow: visible !important; /* Ensure dropdown can overflow */
  }

  /* Override any parent overflow restrictions */
  .navbar,
  .navbar-nav,
  .container,
  .navbar-expand-lg,
  .navbar-dark,
  .navbar-brand,
  .ms-auto,
  .d-flex,
  .align-items-center {
    overflow: visible !important;
  }

  /* Ensure main content doesn't interfere */
  #main-content {
    position: relative;
    z-index: 1;
  }

  .language-dropdown-toggle {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    border-radius: 6px;
    text-decoration: none;
    font-size: 16px;
    transition: all 0.2s ease;
    border: 1px solid transparent;
    background: none;
    cursor: pointer;
    color: inherit;
    background-color: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.2);
  }

  .language-dropdown-toggle:hover {
    background-color: rgba(255, 255, 255, 0.15);
    border-color: rgba(255, 255, 255, 0.4);
  }

  .language-dropdown-toggle:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.3);
  }

  .current-flag {
    font-size: 18px;
  }

  .dropdown-arrow {
    font-size: 12px;
    transition: transform 0.2s ease;
  }

  .language-dropdown-toggle[aria-expanded="true"] .dropdown-arrow {
    transform: rotate(180deg);
  }

  /* Expanded mode for Account Settings */
  .language-switcher-expanded {
    width: 100%;
  }

  .language-toggle-expanded {
    width: 100%;
    justify-content: center;
    padding: 8px 16px;
    min-width: 150px;
  }

  .current-language-name {
    font-size: 14px;
    margin-left: 8px;
    color: inherit;
  }

  .language-dropdown-menu {
    position: fixed;
    min-width: 140px;
    background-color: rgba(33, 37, 41, 0.98) !important;
    border: 1px solid rgba(255, 255, 255, 0.3) !important;
    border-radius: 8px;
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.5) !important;
    backdrop-filter: blur(15px);
    z-index: 999999 !important;
    margin: 0 !important;
    padding: 4px 0;
    list-style: none !important;
    margin-bottom: 0;
    display: none !important;
    transform: translateZ(0);
    will-change: transform;
    isolation: isolate !important; /* Create new stacking context */
    contain: layout style paint !important; /* Optimize rendering */
  }

  .language-dropdown-menu ul {
    list-style: none !important;
    margin: 0 !important;
    padding: 0 !important;
  }

  .language-dropdown-menu.show {
    display: block !important;
    animation: dropdownFadeIn 0.2s ease-out;
  }

  .language-dropdown-menu li {
    margin: 0;
    list-style: none; /* Remove bullet points */
  }

  .language-form {
    display: block;
    margin: 0;
    width: 100%;
  }

  .language-option {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 8px 16px;
    border: none;
    background: none;
    color: inherit;
    text-decoration: none;
    font-size: 14px;
    transition: all 0.2s ease;
    cursor: pointer;
    text-align: left;
    list-style: none; /* Remove bullet points */
  }

  .language-option:hover {
    background-color: rgba(255, 255, 255, 0.1);
    color: inherit;
  }

  .language-option.active {
    background-color: rgba(255, 255, 255, 0.15);
    color: #f7f6f4;
  }

  .language-option .flag {
    font-size: 16px;
    width: 20px;
    text-align: center;
  }

  .language-option .language-name {
    flex: 1;
  }

  /* Ensure dropdown appears above other elements */
  .language-dropdown-menu.show {
    display: block !important;
    animation: dropdownFadeIn 0.2s ease-out;
  }

  @keyframes dropdownFadeIn {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`}</style>
    </>
  )
}
